import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { toast } from "sonner";

// Self-contained chat panel used on the Parent Dashboard "My RSVPs" detail
// dialog. Mirrors the chat behaviour on the Meet-ups page (8s polling,
// module-scoped input state, autofocus, spacebar-safe) without importing
// or modifying Meetups.jsx.
function ChatInput({ eventId, onSend }) {
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const ok = await onSend(trimmed);
    if (ok) setText("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  return (
    <div className="flex gap-2">
      <Input
        ref={inputRef}
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === " ") e.stopPropagation();
          if (e.key === "Enter") { e.preventDefault(); submit(); }
        }}
        placeholder="Ask a question or say hi..."
        className="rounded-full bg-white border-sage/30"
        data-testid={`rsvp-chat-input-${eventId}`}
      />
      <Button size="sm" onClick={submit} disabled={!text.trim()} className="rounded-full bg-sage hover:bg-sage-dark text-white shrink-0" data-testid={`rsvp-chat-send-${eventId}`}>
        <Send className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function MeetupChatPanel({ eventId, user, hostUserId }) {
  const [messages, setMessages] = useState([]);
  const endRef = useRef(null);

  useEffect(() => {
    if (!eventId) return;
    let active = true;
    const fetchChat = () =>
      api.get(`/meetups/${eventId}/chat`)
        .then((r) => { if (active) setMessages(r.data); })
        .catch(() => {});
    fetchChat();
    // Clear the unread meet-up-chat notification for this meet-up while
    // the panel is open / polling.
    api.post(`/meetups/${eventId}/chat/read`).catch(() => {});
    const t = setInterval(() => {
      fetchChat();
      api.post(`/meetups/${eventId}/chat/read`).catch(() => {});
    }, 8000);
    return () => { active = false; clearInterval(t); };
  }, [eventId]);

  const send = async (text) => {
    try {
      await api.post(`/meetups/${eventId}/chat`, { text });
      const { data } = await api.get(`/meetups/${eventId}/chat`);
      setMessages(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Could not send message"));
      return false;
    }
  };

  return (
    <div className="bg-softstone/60 rounded-2xl p-4 overflow-hidden min-w-0" data-testid={`rsvp-chat-panel-${eventId}`}>
      <div className="max-h-72 overflow-y-auto space-y-3 mb-3 pr-1">
        {messages.length === 0 && (
          <p className="text-xs text-forest/50 py-2">No messages yet — ask the host a question!</p>
        )}
        {messages.map((msg) => {
          const own = user && msg.user_id === user.user_id;
          return (
            <div key={msg.message_id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${own ? "bg-sage text-white" : "bg-white border border-sage/20"}`}>
                <p className={`text-[10px] font-bold mb-0.5 ${own ? "text-white/70" : "text-terracotta-dark"}`}>
                  {msg.name}{hostUserId && msg.user_id === hostUserId ? " · host" : ""}
                </p>
                <p className="text-sm leading-snug whitespace-pre-wrap break-words">{msg.text}</p>
                <p className={`text-[9px] mt-1 ${own ? "text-white/60" : "text-forest/40"}`}>
                  {new Date(msg.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <ChatInput eventId={eventId} onSend={send} />
    </div>
  );
}
